import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

// Core toast variants. One name per concept, no aliases.
export type ToastVariant = 'success' | 'error' | 'info' | 'warn' | 'danger';

export type Toast = {
  id: string;
  title?: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
  action?: { label: string; onClick: () => void };
};

export type ToastContextValue = {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  /** Shorthand for addToast; returns id. Use for compatibility with code that expects useToast().toast(). */
  toast: (opts: Omit<Toast, 'id'>) => string;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

function generateId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = generateId();
    const toastWithDefaults: Toast = {
      id,
      variant: toast.variant ?? 'info',
      durationMs: toast.action ? 0 : (toast.durationMs ?? 3500),
      ...toast,
    };
    setToasts(prev => [...prev, toastWithDefaults]);
    if (toastWithDefaults.durationMs && toastWithDefaults.durationMs > 0) {
      window.setTimeout(() => removeToast(id), toastWithDefaults.durationMs);
    }
    return id;
  }, [removeToast]);

  const clearToasts = useCallback(() => setToasts([]), []);

  const toast = useCallback((opts: Omit<Toast, 'id'>) => addToast(opts), [addToast]);
  const value = useMemo(() => ({ toasts, addToast, removeToast, clearToasts, toast }), [toasts, addToast, removeToast, clearToasts, toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
};

const iconMap: Record<ToastVariant, React.ReactNode> = {
  success: (
    // check icon
    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-7.25 7.25a1 1 0 01-1.414 0l-3-3a1 1 0 011.414-1.414L8.5 11.086l6.543-6.543a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  ),
  error: (
    // x icon
    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1.707-6.293a1 1 0 001.414 1.414L10 11.414l.293.293a1 1 0 001.414-1.414L11.414 10l.293-.293a1 1 0 00-1.414-1.414L10 8.586l-.293-.293a1 1 0 00-1.414 1.414L8.586 10l-.293.293z" clipRule="evenodd" />
    </svg>
  ),
  info: (
    // info icon
    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path fillRule="evenodd" d="M18 10A8 8 0 11.001 10 8 8 0 0118 10zM9 7a1 1 0 112 0 1 1 0 01-2 0zm1 3a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  ),
  warn: (
    // exclamation icon
    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.518 11.59C19.021 16.44 18.262 18 16.77 18H3.23c-1.492 0-2.25-1.56-1.491-3.311l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-2a1 1 0 01-1-1V8a1 1 0 112 0v3a1 1 0 01-1 1z"
        clipRule="evenodd"
      />
    </svg>
  ),
  // "danger" shares the error icon for consistency
  danger: (
    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1.707-6.293a1 1 0 001.414 1.414L10 11.414l.293.293a1 1 0 001.414-1.414L11.414 10l.293-.293a1 1 0 00-1.414-1.414L10 8.586l-.293-.293a1 1 0 00-1.414 1.414L8.586 10l-.293.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

const ToastViewport: React.FC<{ toasts: Toast[]; onDismiss: (id: string) => void }> = ({ toasts, onDismiss }) => {
  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-toast max-w-sm ${toasts.length === 0 ? 'pointer-events-none' : 'pointer-events-auto'}`}
      aria-live="polite" 
      aria-relevant="additions" 
      role="status"
    >
      {toasts.map(t => (
        <div key={t.id} className="rounded-lg border border-border bg-card text-foreground px-4 py-3 flex items-start gap-3 shadow-lg animate-in slide-in-from-right-5 duration-200">
          <div className={`mt-0.5 ${
            t.variant === 'success' ? 'text-success' :
            t.variant === 'error' || t.variant === 'danger' ? 'text-danger' :
            t.variant === 'warn' ? 'text-warn' :
            'text-accent'
          }`}>{iconMap[t.variant || 'info']}</div>
          <div className="flex-1 min-w-0">
            {t.title && <div className="font-medium text-sm truncate">{t.title}</div>}
            {t.description && <div className="text-sm opacity-90 break-words">{t.description}</div>}
          </div>
          {t.action && (
            <button className="ml-2 text-xs font-medium underline underline-offset-2" onClick={t.action.onClick} aria-label={t.action.label}>
              {t.action.label}
            </button>
          )}
          <button aria-label="Dismiss" className="text-inherit/70 hover:text-inherit" onClick={() => onDismiss(t.id)}>
            <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

// Export Toast component for use in feedback.tsx
export type ToastProps = {
  title?: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warn' | 'info' | 'danger';
  duration?: number;
  action?: { label: string; onClick: () => void };
  onClose?: () => void;
  className?: string;
};

export const Toast: React.FC<ToastProps> = ({ title, description, variant = 'info', onClose, className = '' }) => {
  const effectiveVariant = variant === 'default' ? 'info' : variant === 'danger' ? 'error' : variant;
  const icon = iconMap[effectiveVariant] || iconMap.info;

  const iconColor = effectiveVariant === 'success' ? 'text-success' :
    effectiveVariant === 'error' ? 'text-danger' :
    effectiveVariant === 'warn' ? 'text-warn' :
    'text-accent';

  return (
    <div
      data-component="Toast"
      data-package="@appic/ui-components"
      className={`rounded-lg border border-border bg-card text-foreground px-4 py-3 flex items-start gap-3 shadow-lg ${className}`}
    >
      <div className={`mt-0.5 ${iconColor}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        {title && <div className="font-medium text-sm truncate">{title}</div>}
        {description && <div className="text-sm opacity-90 break-words">{description}</div>}
      </div>
      {onClose && (
        <button aria-label="Dismiss" className="text-inherit/70 hover:text-inherit" onClick={onClose}>
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </div>
  );
};
